import AddFolderBtn from '@Resources/assets/icons/dark/addFolder.svg';
import UploadDataBtn from '@Resources/assets/icons/dark/cloud2.svg';
import Flex from '@Resources/styles/flex.scss';
import ColorsStyles from '@Styles/colors.scss';
import { useAuth0 } from '@auth0/auth0-react';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Divider,
    Menu,
    MenuItem,
    Skeleton,
    SvgIcon,
    Tooltip,
} from '@mui/material';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IconButton } from '@Components/IconButton';
import DownloadReportsDialogue from '@Components/ModalInterfaces/DownloadReports/DownloadReportsDialogue';
import JobSettingsDialog from '@Components/ModalInterfaces/JobSettings/JobSettingsDialog';
import { SettingsType } from '@Components/Settings/SettingsType';
import EntitiesApi from '@Data/entities/Api';
import FilesApi from '@Data/files/Api';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import {
    foldersState,
    selectedFolderIdState,
    selectedFolderIdsState,
} from '@Data/folders/Reducer';
import { default as ProcessesApi } from '@Data/processes/Api';
import { tryCreateProcessNotificationForTask } from '@Data/processes/Helpers';
import { IProcess, IRunningProcess } from '@Data/processes/Interfaces';
import { linkDepartmentProcessState } from '@Data/processes/Reducer';
import { IProject } from '@Data/projects/Interfaces';
import { ProjectActionType } from '@Data/projects/ProjectActionTypes';
import { QualityControlApi } from '@Data/qualityControl/Api';
import { qualityControlGeoserverFeaturesState } from '@Data/qualityControl/Reducer';
import { SettingsApi } from '@Data/settings/Api';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';
import { KoruRoutes } from '@Services/navigation/Paths';
import { info } from '@Services/notification/NotificationServices';
import ManageFolderDialogue from '@Views/Project/components/Drawers/ContentsDrawer/ManageFolderDialogue';
import ProjectModalDialog from '@Views/Project/components/ProjectModalDialogue/ProjectModalDialogue';
import FoldersDataGrid from './FoldersDataGrid';
import Styles from './FoldersPanel.scss';

interface IFoldersPanelProps {
    selectedProject: IProject;
    isLoading: boolean;
    onManageProject: () => void;
}

const FoldersPanel = (props: IFoldersPanelProps) => {
    const { selectedProject, onManageProject, isLoading } = props;
    const { organizationId, teamId, folderIds, projectId } = useParams();
    const features = useSelector(qualityControlGeoserverFeaturesState);

    const navigate = useNavigate();

    const _folders: Array<IFolder> = useSelector(foldersState);
    const selectedFolderIds = useSelector(selectedFolderIdsState);

    const linkDepartmentProcess = useSelector(linkDepartmentProcessState);

    const selectedFolderId: string = useSelector(selectedFolderIdState);

    const [isInitiating, setIsInitiating] = useState(false);

    const [selectedFolder, setSelectedFolder] = useState<IFolder>(null);
    const [isDownloadReportsOpen, setIsDownloadReportsOpen] = useState(false);
    const [isDownloadFilesOpen, setIsDownloadFilesOpen] =
        useState<boolean>(false);
    const [openDialogueType, setOpenDialogueType] =
        useState<ProjectActionType | null>(null);
    const [isManageDialogueOpen, setIsManageDialogueOpen] =
        useState<boolean>(false);
    const [dataMenuIsOpen, setDataMenIsOpen] = useState<boolean>(false);
    const [isJobSettingsOpen, setIsJobSettingsOpen] = useState(false);
    const [isFolderStatusDialogOpen, setIsFolderStatusDialogOpen] =
        useState<boolean>(false);

    const [disableButton, setDisableButton] = useState<boolean>(true);
    const handleConnectDeptClick = () => {
        setOpenDialogueType(ProjectActionType.CONNECT_DEPT);
    };
    const handleCancelManage = () => {
        setIsManageDialogueOpen(false);
        setSelectedFolder(null);
    };

    const handleSubmitManage = (folderName: string, manageFolder: IFolder) => {
        const cb = () => {
            setIsManageDialogueOpen(false);
            onManageProject();
        };

        if (!_isNil(manageFolder)) {
            FoldersApi.editFolderName(folderName, manageFolder, cb);
        } else {
            FoldersApi.createFolder(folderName, selectedProject.project_id, cb);
        }
    };

    useEffect(() => {
        ProcessesApi.getProcesses();
    }, [selectedProject]);

    const handleClick = (folder: IFolder) => {
        if (!isInitiating) {
            // think this is deprecated
            //FoldersApi.setSelectedFolder(folder.folder_id);
            //FoldersApi.toggleFolderVisibility(folder);
            //FoldersApi.zoomToBounds(folder.folder_id, [folder.folder_id]);
        }
    };

    const initiateQc = async () => {
        QualityControlApi.reset();
        EntitiesApi.clearEntity();

        if (features.length > 0) {
            QualityControlApi.initialize(features, true, selectedFolderId);
        } else {
            info('There are no poles to QC');
        }
        navigate(
            KoruRoutes.qc.getUrl(
                organizationId,
                teamId,
                selectedProject.project_id,
                [selectedFolderId]
            )
        );
    };

    useEffect(() => {
        setDisableButton(
            _isNil(selectedFolderId) ||
                isLoading ||
                features.length === 0 ||
                selectedFolderIds.length > 1
        );
    }, [features, isLoading, selectedFolderId, selectedFolderIds]);

    const startQcProcess = async () => {
        QualityControlApi.setInspectionStartDate(new Date().toISOString());
        setIsInitiating(true);
        await initiateQc();
        setIsInitiating(false);
    };

    const onViewFiles = (folder: any) => {
        setIsDownloadReportsOpen(true);
        setSelectedFolder(folder);
    };

    const onJobSettingsClick = (folder: any) => {
        setIsJobSettingsOpen(true);
        setSelectedFolder(folder);
    };

    const closeDialogs = () => {
        setIsDownloadReportsOpen(false);
        setIsJobSettingsOpen(false);
        setSelectedFolder(null);
        setIsFolderStatusDialogOpen(false);
    };

    const onComplete = async () => {
        onManageProject();
        closeDialogs();
    };

    const handleOpenFolderStatusDialog = (folder: IFolder) => {
        setSelectedFolder(folder);
        setIsFolderStatusDialogOpen(true);
    };

    const title = React.useMemo(() => {
        let titleElement =
            isLoading || _isNil(selectedProject) ? (
                <Skeleton
                    variant="text"
                    animation="wave"
                    width={100}
                    className={cx(Flex.flex)}
                />
            ) : (
                <Tooltip title="Synced IKE Office dept">
                    <span id="qc-dept-name" className={cx(Flex.flex)}>
                        {selectedProject.department_name}
                    </span>
                </Tooltip>
            );
        return titleElement;
    }, [isLoading, selectedProject]);

    const handleOpenProjectSettingsDialog = () => {
        SettingsApi.openSettings(SettingsType.PROJECT);
    };
    const toggleDataMenu = () => {
        setDataMenIsOpen(!dataMenuIsOpen);
    };
    const uploadDataBtnId = 'upload-data-btn';
    const projectSettingsBtnId = 'project-settings-btn';
    const projectSettingsIcon = (
        <IconButton
            onClick={handleOpenProjectSettingsDialog}
            id={projectSettingsBtnId}
            tooltip={'Project Settings'}
            icon={
                <SvgIcon inheritViewBox>
                    <SettingsApplicationsOutlinedIcon
                        fontSize="small"
                        color="gradientGrey"
                    />
                </SvgIcon>
            }
            key={'projectSettingsIconButton'}
        />
    );
    const uploadData = (
        <IconButton
            onClick={toggleDataMenu}
            id={uploadDataBtnId}
            tooltip="Add Data"
            icon={
                <SvgIcon inheritViewBox>
                    <UploadDataBtn />
                </SvgIcon>
            }
            key={'uploadDataIconButton'}
        />
    );
    const handleFileUploadBtnClick = () => {
        setOpenDialogueType(ProjectActionType.UPLOAD_FILE);
    };

    const uploadDataMenu = (
        <Menu
            id="upload-data-menu"
            open={dataMenuIsOpen}
            onClose={toggleDataMenu}
            anchorEl={document.getElementById(uploadDataBtnId)}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            keepMounted
            key={'uploadDataMenu'}
        >
            <MenuItem
                id="upload-data-option"
                onClick={() => {
                    handleFileUploadBtnClick();
                    toggleDataMenu();
                }}
                dense
            >
                Upload Data
            </MenuItem>
            <Divider />
            <MenuItem
                id="connect-dept-option"
                onClick={() => {
                    handleConnectDeptClick();
                    toggleDataMenu();
                }}
                disabled={isLoading || !_isNil(selectedProject?.department_id)}
                dense
            >
                Connect IKE Office Department
            </MenuItem>
        </Menu>
    );
    const handleClearVisibleFolders = () => {
        FoldersApi.clearSelectedState();
        navigate(
            KoruRoutes.qc.getUrl(
                organizationId,
                teamId,
                selectedProject.project_id,
                []
            )
        );
    };

    const handleCreateFolderBtnClick = () => {
        setSelectedFolder(null);
        setIsManageDialogueOpen(true);
    };
    const createFolder = (
        <IconButton
            id="create-folder-btn"
            onClick={handleCreateFolderBtnClick}
            tooltip="Create Folder"
            icon={
                <SvgIcon inheritViewBox>
                    <AddFolderBtn />
                </SvgIcon>
            }
            key={'createFolderIconButton'}
        />
    );

    const handleProcessFolder = (folder: IFolder) => {
        setSelectedFolder(folder);
        setOpenDialogueType(ProjectActionType.OPEN_PROCESS);
    };

    const { user } = useAuth0();

    const handleAutoQc = (folder: IFolder, iiqAutomationTask: IProcess) => {
        const autoQcPayload = {
            folderId: folder.folder_id,
            projectId: folder.project_fk,
            automation_task:
                'iiq://ikegps.com/hoa/koru/pre-qc-checks.dag?version=v001',
        };

        ProcessesApi.runProcess(
            iiqAutomationTask,
            autoQcPayload,
            Number(folder.project_fk),
            getParsedAuth0Username(user.sub),
            (runningProcess: IRunningProcess) => {
                tryCreateProcessNotificationForTask(
                    runningProcess.process_name,
                    runningProcess.task_id
                );
            }
        );
    };

    const handleCreateSubset = (folder: IFolder) => {
        setSelectedFolder(folder);
        setOpenDialogueType(ProjectActionType.CREATE_SUBSET);
    };

    const handleShareFolder = (folder: IFolder) => {
        setSelectedFolder(folder);
        FilesApi.downloadProjectFile(folder);
    };

    const iconBtns = (
        <div className={cx(Flex.flex)}>
            {createFolder}
            {uploadData}
            {uploadDataMenu}
            {projectSettingsIcon}
        </div>
    );
    let FoldersDataGridProps = {
        linkDepartmentProcess,
        handleClick,
        onViewFiles,
        onJobSettingsClick,
        handleOpenFolderStatusDialog,
        handleProcessFolder,
        handleAutoQc,
        handleShareFolder,
        handleCreateSubset,
        handleClearVisibleFolders,
    };
    const qcBtn = (
        <LoadingButton
            id="start-qc-btn"
            variant={'contained'}
            loading={isInitiating}
            onClick={startQcProcess}
            className={cx(Flex.flex)}
            sx={{
                margin: '0 16px 8px 0',
                backgroundColor: ColorsStyles.ikeSecondaryDarkGreen,
                fontWeight: '400',
                fontSize: '14px',
                lineHeight: '17px',
                padding: '.25rem .5rem',
                textTransform: 'none',
                '&:hover': {
                    backgroundColor: ColorsStyles.ikeSecondaryLightGreen,
                },
            }}
            disabled={disableButton}
        >
            Start QC
        </LoadingButton>
    );
    const innerHtml = (
        <div className={Styles.foldersPanelContentsContainer}>
            <div
                style={{
                    margin: '5px 5px 5px 5px',
                    width: '1000px',
                }}
                className={cx(
                    Flex.flex,
                    Flex.row,
                    Flex.justifyBetween,
                    Flex.alignItemsStart
                )}
            >
                {title}
                {iconBtns}
                {qcBtn}
            </div>
            <div className={Styles.foldersPanelFoldersContainer}>
                <FoldersDataGrid {...FoldersDataGridProps} />
            </div>
        </div>
    );

    const handleDialogueClose = () => {
        setOpenDialogueType(null);
    };

    const handleCompleteProcessEvent = () => {
        handleDialogueClose();
    };
    console.log('re-rendering folders panel');
    return (
        <React.Fragment>
            <div style={{ height: '100%', width: '1000px' }}>
                <div
                    style={{
                        width: '1000px',
                    }}
                    className={cx(Flex.flex, Flex.justifyBetween)}
                >
                    <DownloadReportsDialogue
                        isOpen={isDownloadReportsOpen}
                        onCancel={() => setSelectedFolder(null)}
                        onComplete={() => setSelectedFolder(null)}
                        selectedFolder={selectedFolder}
                    />
                    <JobSettingsDialog
                        isOpen={isJobSettingsOpen}
                        onCancel={closeDialogs}
                        onComplete={onComplete}
                        selectedFolder={selectedFolder}
                        selectedFolders={selectedFolderIds}
                    />
                    <ManageFolderDialogue
                        selectedFolder={selectedFolder}
                        isOpen={isManageDialogueOpen}
                        onCancel={handleCancelManage}
                        onSubmit={handleSubmitManage}
                    />
                    <ProjectModalDialog
                        selectedFolder={selectedFolder}
                        type={openDialogueType}
                        projectId={Number(projectId)}
                        teamId={teamId}
                        isOpen={!_isNil(openDialogueType)}
                        onCancel={handleDialogueClose}
                        onComplete={handleCompleteProcessEvent}
                    />
                </div>
                {innerHtml}
            </div>
        </React.Fragment>
    );
};

export default FoldersPanel;
